import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Calculator as CalculatorIcon, Ruler, ExternalLink, ChevronDown, ChevronUp, Check, Save, Copy, Building, Flame, Layers, ArrowRight, Download, Trash2, Calendar, Tag } from 'lucide-react';
import CalculatorForm from '../components/CalculatorForm';
import Header from '../components/Header';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '../components/ui/alert-dialog';
import { Separator } from '../components/ui/separator';
import { cn, formatNumber } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { exportToExcel } from '../lib/exportUtils';
import toast from 'react-hot-toast';
import alasAmblem from '../assets/images/alas-beyaz-amblem.svg';
import { useCalculations } from '../lib/hooks/useCalculations';
import i18n from '../i18n';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

// Ürün verileri
import { getProductById, getProductName, getProductDisplayName, getProductShortName, getExtinguisherProducts } from '../lib/productData';

/**
 * Hesaplama sayfası bileşeni
 * @returns {JSX.Element} - Hesaplama sayfası
 */
const Calculator = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [calculationResults, setCalculationResults] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [showSavedCalculations, setShowSavedCalculations] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateRoomData, setDuplicateRoomData] = useState(null);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  
  // Manuel seçim modu için state'ler
  const [selectedMode, setSelectedMode] = useState('automatic');
  const [manualProductId, setManualProductId] = useState('');
  const [manualQuantity, setManualQuantity] = useState(1);
  const [manualSelections, setManualSelections] = useState([]);
  
  const { saveCalculation, calculationHistory, fetchCalculationHistory, deleteCalculation, clearAllCalculations } = useCalculations();
  
  // Genişletilmiş durum değiştirme
  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Sayfa yüklendiğinde kayıtlı hesaplamaları al
  useEffect(() => {
    fetchCalculationHistory();
    
    // Sayfanın ilk yüklendiğinde token ve kullanıcı ID'si kontrolü
    const sessionData = localStorage.getItem('alasfireSession');
    if (!sessionData) {
      navigate('/login');
    }
    
    // Loading durumunu false olarak ayarla
      setLoading(false);
  }, [navigate]);

  // Scroll pozisyonunu takip et
  useEffect(() => {
    const handleScroll = () => {
      const savedSection = document.getElementById('saved-calculations-section');
      if (savedSection) {
        const rect = savedSection.getBoundingClientRect();
        const isNearSavedSection = rect.top <= window.innerHeight * 0.5;
        setIsAtTop(!isNearSavedSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hesaplama sonuçlarını alır
  const handleCalculationResults = (results) => {
    console.log('Hesaplama sonuçları alındı:', results);
    console.log('Results.data:', results?.data);
    console.log('Results.data.selections:', results?.data?.selections);
    
    try {
      if (!results) {
        setCalculationResults(null);
        return;
      }
      
      if (results && results.data) {
        console.log('Setting calculationResults to:', results.data);
        setCalculationResults(results.data);
        setLoading(false);
      } else {
        // Burada gelen sonuç formatını doğrula
        if (results.appliedSelection && results.appliedSelection.algorithm === 'manual') {
          // Manuel seçim için sonuçları doğru şekilde işle
          setCalculationResults({
            ...results,
            selections: {
              ...(results.selections || {}),
              // Manuel seçim sonuçlarını selections.manual altına da ekle
              manual: results.appliedSelection.results
            }
          });
        } else {
          setCalculationResults(results);
        }
      }
      
      setError(null);
    } catch (error) {
      setLoading(false);
      toast({
        title: t("errors.calculation.title"),
        description: error.response?.data?.message || t("errors.calculation.description"),
        variant: "destructive",
      });
      console.error("Hesaplama hatası:", error);
    }
  };

  // Hata mesajını alır
  const handleError = (errorMessage) => {
    // null veya undefined durumunda hata mesajını temizle, console'a yazdırma
    if (!errorMessage) {
      setError(null);
      return;
    }
    
    console.error('Hesaplama hatası:', errorMessage);
    setError(errorMessage);
  };

  // Manuel seçim işlevleri
  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    if (mode === 'manual') {
      // Manuel mod aktifleştiğinde otomatik seçimleri temizle
      if (calculationResults) {
        setCalculationResults({
          ...calculationResults,
          appliedSelection: null,
          manualSelections: manualSelections
        });
      }
    } else if (mode === 'automatic') {
      // Otomatik moda geri dönüldüğünde manuel seçimleri temizle
      setManualSelections([]);
      if (calculationResults && calculationResults.appliedSelection?.algorithm === 'manual') {
        setCalculationResults({
          ...calculationResults,
          appliedSelection: null
        });
      }
    }
  };

  const handleAddManualProduct = () => {
    if (!manualProductId || !manualQuantity || manualQuantity < 1) {
      toast(t('calculator.notifications.invalid_manual_selection'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
      return;
    }

    const product = getProductById(manualProductId);
    if (!product) {
      toast(t('calculator.notifications.product_not_found'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
      return;
    }

    // Mevcut ürün varsa güncelle, yoksa ekle
    const existingIndex = manualSelections.findIndex(sel => sel.id === product.id);
    let newSelections;
    
    if (existingIndex >= 0) {
      newSelections = [...manualSelections];
      newSelections[existingIndex] = {
        ...product,
        quantity: manualQuantity,
        effective_mass: product.effective_mass || 0
      };
    } else {
      newSelections = [...manualSelections, {
        ...product,
        quantity: manualQuantity,
        effective_mass: product.effective_mass || 0
      }];
    }

    setManualSelections(newSelections);
    setManualProductId('');
    setManualQuantity(1);

    // Hesaplamaları güncelle
    updateManualCalculations(newSelections);
  };

  const handleRemoveManualProduct = (productId) => {
    const newSelections = manualSelections.filter(sel => sel.id !== productId);
    setManualSelections(newSelections);
    updateManualCalculations(newSelections);
  };

  const updateManualCalculations = (selections) => {
    if (!calculationResults || !calculationResults.room) return;

    // Kontrol ünitesi sayısını hesapla
    const totalExtinguishers = selections.filter(p => !p.isController).reduce((sum, p) => sum + p.quantity, 0);
    const requiredControllers = Math.ceil(totalExtinguishers / 12);
    
    // Kontrol ünitelerini ekle
    const controllerProduct = getProductById(99);
    const allProducts = [...selections];
    
    if (requiredControllers > 0 && controllerProduct) {
      allProducts.push({
        ...controllerProduct,
        quantity: requiredControllers,
        effective_mass: 0,
        isController: true
      });
    }

    // Toplam etkin kütle hesapla
    const totalMass = selections.reduce((sum, p) => sum + (p.effective_mass * p.quantity), 0);
    
    // Doğrulama oranını hesapla
    const verificationRatio = (totalMass / calculationResults.room.required_mass) * 100;

    // Hesaplama sonuçlarını güncelle
    const manualResults = {
      selectedProducts: allProducts,
      products: allProducts,
      totalMass: totalMass,
      verificationRatio: verificationRatio,
      totalUnits: totalExtinguishers,
      controllerCount: requiredControllers
    };

    setCalculationResults({
      ...calculationResults,
      appliedSelection: {
        algorithm: 'manual',
        results: manualResults
      },
      manualSelections: selections
    });
  };

  // Aktif seçim algoritması belirle
  const getActiveSelection = () => {
    if (!calculationResults) return null;
    
    // Manuel seçim sonuçları (en yüksek öncelik)
    if (calculationResults.appliedSelection && calculationResults.appliedSelection.algorithm === 'manual') {
      return {
        algorithm: 'manual',
        data: calculationResults.appliedSelection.results
      };
    }
    
    // Güvenli Yeni Dinamik Algoritma sonuçları (birincil algoritma)
    if (calculationResults.appliedSelection && calculationResults.appliedSelection.algorithm === 'new_dynamic_safe') {
      return {
        algorithm: 'new_dynamic_safe',
        data: calculationResults.appliedSelection.results
      };
    }
    
    // Otomatik seçim sonuçları (varsa)
    if (calculationResults.appliedSelection) {
      return {
        algorithm: calculationResults.appliedSelection.algorithm,
        data: calculationResults.appliedSelection.results
      };
    }
    
    // Doğrudan güvenli yeni dinamik algoritma sonuçları
    if (calculationResults.selections && calculationResults.selections.new_dynamic_safe) {
      return {
        algorithm: 'new_dynamic_safe',
        data: calculationResults.selections.new_dynamic_safe
      };
    }
    
    // Excel Complete Algorithm sonuçları (geriye dönük uyumluluk)
    if (calculationResults.selections && calculationResults.selections.excel_complete) {
      return {
        algorithm: 'excel_complete',
        data: calculationResults.selections.excel_complete
      };
    }
    
    // Greedy algoritması (eski uyumluluk)
    if (calculationResults.selections && calculationResults.selections.greedy) {
      return {
        algorithm: 'greedy',
        data: calculationResults.selections.greedy
      };
    }
    
    // Manuel seçim sonuçları (geriye dönük uyumluluk)
    if (calculationResults.selections && calculationResults.selections.manual) {
      return {
        algorithm: 'manual',
        data: calculationResults.selections.manual
      };
    }
    
    return null;
  };
  
  // Doğrulama oranını hesapla ve göster (Sadece kütle bazlı)
  const renderVerificationRatio = (selection, roomVolume, fireClass, requiredMass) => {
    if (!selection) return null;
    
    // Kütle bazlı doğrulama oranı hesaplama
    let verificationRatio = null;
    
    // Önce direkt verificationRatio değerini kontrol et
    if (selection.verificationRatio !== undefined) {
      verificationRatio = typeof selection.verificationRatio === 'string' 
        ? parseFloat(selection.verificationRatio) 
        : selection.verificationRatio;
    }
    // Eğer verificationRatio yoksa manuel hesaplama yap
    else if (selection.totalEffectiveMass && requiredMass) {
      verificationRatio = (selection.totalEffectiveMass / requiredMass) * 100;
    }
    
    if (verificationRatio === null) return null;
    
    // Renk ve durumları belirle
    let color = 'text-green-600';
    let bgColor = 'bg-green-50';
    let borderColor = 'border-green-200';
    
    if (verificationRatio < 100) {
      color = 'text-red-600';
      bgColor = 'bg-red-50';
      borderColor = 'border-red-200';
    } else if (verificationRatio < 124) {
      color = 'text-amber-600';
      bgColor = 'bg-amber-50';
      borderColor = 'border-amber-200';
    } else if (verificationRatio > 150) {
      color = 'text-blue-600';
      bgColor = 'bg-blue-50';
      borderColor = 'border-blue-200';
    }
    
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className={`font-semibold ${color}`}>
            {formatNumber(verificationRatio)}%
          </span>
          {verificationRatio >= 100 && verificationRatio <= 150 && (
            <Badge variant="outline" className={`${bgColor} ${color} ${borderColor}`}>
              <Check className="h-3 w-3 mr-1" />
              {verificationRatio >= 124 && verificationRatio <= 144 ? t('calculator.optimal') : t('calculator.sufficient')}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  // Sonuçları kopyalama işlevi
  const handleCopyResults = (algorithm) => {
    try {
      if (!calculationResults) return;
      
      // Hangi algoritmanın sonuçlarını kopyalayacağımızı belirle
      let selection;
      
      if (algorithm === 'manual' && calculationResults.appliedSelection && calculationResults.appliedSelection.algorithm === 'manual') {
        selection = calculationResults.appliedSelection.results;
      } else if (algorithm === 'manual' && calculationResults.selections && calculationResults.selections.manual) {
        selection = calculationResults.selections.manual;
      } else if (algorithm === 'new_dynamic_safe' && calculationResults.appliedSelection && calculationResults.appliedSelection.algorithm === 'new_dynamic_safe') {
        selection = calculationResults.appliedSelection.results;
      } else if (algorithm === 'new_dynamic_safe' && calculationResults.selections && calculationResults.selections.new_dynamic_safe) {
        selection = calculationResults.selections.new_dynamic_safe;
      } else if (algorithm === 'excel_complete' && calculationResults.selections && calculationResults.selections.excel_complete) {
        selection = calculationResults.selections.excel_complete;
      } else if (algorithm === 'greedy' && calculationResults.selections && calculationResults.selections.greedy) {
        selection = calculationResults.selections.greedy;
      } else if (calculationResults.appliedSelection) {
        selection = calculationResults.appliedSelection.results;
      } else if (calculationResults.selections && calculationResults.selections.excel_complete) {
        selection = calculationResults.selections.excel_complete;
      } else if (calculationResults.selections && calculationResults.selections.greedy) {
        selection = calculationResults.selections.greedy;
      } else {
        toast(t('calculator.notifications.no_results_to_copy'), {
          id: 'copy-error',
          icon: '❌',
          style: {
            backgroundColor: '#F8D7DA',
            color: '#721C24',
            border: '1px solid #F5C6CB'
          }
        });
        return;
      }
      
      // Oda bilgileri
      const room = calculationResults.room;
      if (!room) {
        toast(t('calculator.notifications.missing_room_data'), {
          id: 'copy-error',
          icon: '❌',
          style: {
            backgroundColor: '#F8D7DA',
            color: '#721C24',
            border: '1px solid #F5C6CB'
          }
        });
        return;
      }
      
      // Kopyalanacak metni oluştur
      let copyText = `=== ${t('calculator.copyExport.calculation_results_title')} ===\n\n`;
      
      // Proje bilgileri
      if (calculationResults.project_name) copyText += `${t('calculator.copyExport.project')}: ${calculationResults.project_name}\n`;
      if (calculationResults.region_name) copyText += `${t('calculator.copyExport.region')}: ${calculationResults.region_name}\n`;
      if (calculationResults.company_name) copyText += `${t('calculator.copyExport.company')}: ${calculationResults.company_name}\n`;
      if (calculationResults.room_name) copyText += `${t('calculator.copyExport.room')}: ${calculationResults.room_name}\n\n`;
      
      // Oda boyutları - güvenli değer kontrolü ile
      if (room.length !== undefined && room.width !== undefined && room.height !== undefined) {
        copyText += `${t('calculator.copyExport.room_dimensions')}: ${room.length.toFixed(2)}m × ${room.width.toFixed(2)}m × ${room.height.toFixed(2)}m\n`;
      }
      
      if (room.volume !== undefined) {
        copyText += `${t('calculator.copyExport.volume')}: ${room.volume.toFixed(2)} m³\n`;
      }
      
      if (room.adjusted_volume !== undefined) {
        copyText += `${t('calculator.copyExport.adjusted_volume')}: ${room.adjusted_volume.toFixed(2)} m³\n`;
      }
      
      if (room.safety_factor !== undefined) {
        copyText += `${t('calculator.copyExport.safety_factor')}: ${room.safety_factor.toFixed(2)}\n`;
      }
      
      if (room.fire_class) {
        copyText += `${t('calculator.copyExport.fire_class')}: ${t(`calculator.copyExport.fire_classes.${room.fire_class}`)}\n`;
      }
      
      if (room.required_mass !== undefined) {
        copyText += `${t('calculator.copyExport.required_extinguisher')}: ${room.required_mass.toFixed(2)} g\n\n`;
      } else if (room.required_extinguisher !== undefined) {
        copyText += `${t('calculator.copyExport.required_extinguisher')}: ${room.required_extinguisher.toFixed(2)} g\n\n`;
      } else {
        copyText += `${t('calculator.copyExport.required_extinguisher')}: Hesaplanamadı\n\n`;
      }
      
      // Ürün seçimleri
      copyText += `=== ${t('calculator.copyExport.product_selection')} ===\n`;
      if (selection && selection.products) {
        selection.products.forEach(product => {
          if (product.isController) {
            copyText += `${product.quantity}x ${t('calculator.copyExport.control_unit')}\n`;
          } else {
            copyText += `${product.quantity}x ${getProductShortName(product, i18n.language)} (${product.effective_mass} g)\n`;
          }
        });
      } else if (selection && selection.selectedProducts) {
        selection.selectedProducts.forEach(product => {
          if (product.isController) {
            copyText += `${product.quantity}x ${t('calculator.copyExport.control_unit')}\n`;
          } else {
            copyText += `${product.quantity}x ${getProductShortName(product, i18n.language)} (${product.effective_mass} g)\n`;
          }
        });
      } else {
        copyText += `${t('calculator.no_products_selected')}\n`;
      }
      
      // Toplam bilgiler
      copyText += `\n${t('calculator.copyExport.total_extinguisher_count')}: ${selection.totalCount || 'Belirtilmemiş'}\n`;
      
      if (selection.totalEffectiveMass !== undefined) {
        copyText += `${t('calculator.copyExport.total_effective_mass')}: ${selection.totalEffectiveMass.toFixed(2)} g\n`;
      }
      
      // Doğrulama oranı - tutarlı kütle bazlı hesaplama
      let verificationRatio = null;
      
      // Öncelikle direkt verificationRatio değerini kullan
      if (selection.verificationRatio !== undefined) {
        verificationRatio = typeof selection.verificationRatio === 'string' 
          ? parseFloat(selection.verificationRatio) 
          : selection.verificationRatio;
      }
      // Eğer verificationRatio yoksa manuel hesaplama yap
      else if (selection.totalEffectiveMass && room.required_mass) {
        verificationRatio = (selection.totalEffectiveMass / room.required_mass) * 100;
      }
      
      // Doğrulama oranını ekle
      if (verificationRatio !== null) {
        copyText += `${t('calculator.copyExport.verification_ratio')}: ${verificationRatio.toFixed(2)}%\n`;
      }
      
      // Tarih - dile göre formatla
      const currentLang = i18n.language || 'tr';
      const locale = currentLang === 'en' ? 'en-US' : 'tr-TR';
      copyText += `\n${t('calculator.copyExport.calculation_date')}: ${new Date().toLocaleString(locale)}\n`;
      
      // Metni panoya kopyala
      navigator.clipboard.writeText(copyText)
        .then(() => {
          toast(t('calculator.notifications.results_copied'), {
            id: 'copy-success',
            icon: '✅',
            style: {
              backgroundColor: '#D4EDDA',
              color: '#155724',
              border: '1px solid #C3E6CB'
            }
          });
        })
        .catch((err) => {
          console.error('Kopyalama hatası:', err);
          toast(t('calculator.notifications.copy_failed'), {
            id: 'copy-error',
            icon: '❌',
            style: {
              backgroundColor: '#F8D7DA',
              color: '#721C24',
              border: '1px solid #F5C6CB'
            }
          });
        });
        
    } catch (error) {
      console.error('Kopyalama işlemi sırasında hata oluştu:', error);
      toast(t('calculator.notifications.copy_failed'), {
        id: 'copy-error',
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
    }
  };

  // Toplu kopyalama işlemi
  const handleBulkCopy = () => {
    if (!calculationHistory || calculationHistory.length === 0) {
      toast(t('calculator.notifications.no_calculations_to_copy'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
      return;
    }

    try {
      let copyText = '';
      
      calculationHistory.forEach((calculation, index) => {
        const room = calculation.room;
        const selection = calculation.selection;
        
        // Oda numarası ekle
        copyText += t('calculator.bulk_copy_template.room_number', { number: index + 1 }) + '\n';
        
        // Yeni format ile kopyalama metni oluştur
        copyText += `${t('calculator.bulk_copy_template.header')}\n\n`;
        
        // Oda bilgileri
        copyText += t('calculator.bulk_copy_template.dimensions', {
          length: formatNumber(room.length),
          width: formatNumber(room.width),
          height: formatNumber(room.height)
        }) + '\n';
        
        copyText += t('calculator.bulk_copy_template.volume', {
          volume: formatNumber(room.volume)
        }) + '\n';
        
        copyText += t('calculator.bulk_copy_template.adjusted_volume', {
          adjusted_volume: formatNumber(room.adjusted_volume)
        }) + '\n';
        
        copyText += t('calculator.bulk_copy_template.safety_factor', {
          safety_factor: formatNumber(room.safety_factor)
        }) + '\n';
        
        // Yangın sınıfı
        let fireClassName = '';
        if (room.fire_class === 'a-wooden-stack') 
          fireClassName = t('calculator.fire_class_a');
        else if (room.fire_class === 'b-nheptane-liquid') 
          fireClassName = t('calculator.fire_class_b');
        else if (room.fire_class === 'a-polymer-sheet') 
          fireClassName = t('calculator.fire_class_a_polymer');
        else if (room.fire_class === 'wooden-stack-general') 
          fireClassName = t('calculator.fire_class_wooden_general');
        
        copyText += t('calculator.bulk_copy_template.fire_class', {
          fire_class: fireClassName
        }) + '\n';
        
        copyText += t('calculator.bulk_copy_template.required_mass', {
          required_mass: formatNumber(room.required_mass)
        }) + '\n\n';
        
        // Ürün seçimi
        copyText += t('calculator.bulk_copy_template.product_selection_header') + '\n';
        
        if (selection && selection.results) {
          const products = selection.results.products || selection.results.selectedProducts || [];
          
          products.filter(p => !p.isController).forEach(product => {
            copyText += `${product.quantity}x ${getProductName(product, i18n.language)}\n`;
          });
          
          // Kontrol ünitesi varsa ekle
          const controllerProduct = products.find(p => p.isController);
          if (controllerProduct) {
            copyText += `${controllerProduct.quantity}x ${getProductName(controllerProduct, i18n.language)}\n`;
          }
          
          const totalUnits = products.filter(p => !p.isController).reduce((sum, p) => sum + p.quantity, 0);
          const totalEffectiveMass = products.filter(p => !p.isController).reduce((sum, p) => sum + (p.effective_mass * p.quantity), 0);
          
          copyText += '\n';
          copyText += t('calculator.bulk_copy_template.total_extinguishers', {
            count: totalUnits
          }) + '\n';
          
          copyText += t('calculator.bulk_copy_template.total_effective_mass', {
            mass: formatNumber(totalEffectiveMass)
          }) + '\n';
          
          copyText += t('calculator.bulk_copy_template.verification_ratio', {
            ratio: formatNumber(selection.results.verificationRatio || 0)
          }) + '\n';
        }
        
        // Tarih - dil bazlı format
        const currentLanguage = i18n.language || 'tr';
        const locale = currentLanguage === 'en' ? 'en-US' : 'tr-TR';
        copyText += t('calculator.bulk_copy_template.calculation_date', {
          date: new Date(calculation.timestamp).toLocaleString(locale)
        }) + '\n\n\n';
      });
      
      navigator.clipboard.writeText(copyText);
      toast(t('calculator.notifications.bulk_copy_success'), {
        icon: '✅',
        style: {
          backgroundColor: '#D4EDDA',
          color: '#155724',
          border: '1px solid #C3E6CB'
        }
      });
    } catch (error) {
      console.error('Toplu kopyalama sırasında hata:', error);
      toast(t('calculator.notifications.bulk_copy_error'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
    }
  };
  
  // Toplu Excel raporu oluşturma
  const handleBulkExport = () => {
    if (!calculationHistory || calculationHistory.length === 0) {
      toast(t('calculator.notifications.no_calculations_to_export'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
      return;
    }

    try {
      exportToExcel(
        { bulkExport: true, calculations: calculationHistory },
        'Alas_Fire_Toplu_Rapor',
        t('calculator.bulk_export_title'),
        t,
        i18n.language
      );
    } catch (error) {
      console.error('Toplu Excel raporu oluşturulurken hata:', error);
      toast(t('calculator.notifications.bulk_export_error'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
    }
  };

  const handleSaveCalculation = async (algorithm) => {
    if (!calculationResults || !calculationResults.room) {
      toast(t('calculator.notifications.no_results_to_save'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
      return;
    }

    // Aktif seçimi belirle
    let selection;
    if (algorithm === 'manual' && calculationResults.appliedSelection?.algorithm === 'manual') {
      selection = calculationResults.appliedSelection;
    } else if (algorithm === 'new_dynamic_safe') {
      selection = calculationResults.appliedSelection?.algorithm === 'new_dynamic_safe' 
        ? calculationResults.appliedSelection 
        : { algorithm: 'new_dynamic_safe', results: calculationResults.selections.new_dynamic_safe };
    } else if (algorithm === 'excel_complete') {
      selection = { algorithm: 'excel_complete', results: calculationResults.selections.excel_complete };
    } else if (algorithm === 'greedy') {
      selection = { algorithm: 'greedy', results: calculationResults.selections.greedy };
    }

    // Kaydedilecek veriyi hazırla
    const roomData = {
      ...calculationResults.room,
      room_name: calculationResults.room_name || 'İsimsiz Oda',
      project_name: calculationResults.project_name || 'İsimsiz Proje'
    };
    
    const calculationData = {
      room: roomData,
      project_name: calculationResults.project_name || 'İsimsiz Proje',
      region_name: calculationResults.region_name || '',
      company_name: calculationResults.company_name || '',
      room_name: calculationResults.room_name || 'İsimsiz Oda',
      selectedAlgorithm: algorithm,
      selection,
      timestamp: new Date().toISOString(),
    };

    try {
      const result = await saveCalculation(calculationData, false); // updateMode = false
      
      if (result.success) {
        const messageKey = result.updated ? 'calculation_updated' : 'calculation_saved';
        toast(t(`calculator.notifications.${messageKey}`), {
          icon: '✅',
          style: {
            backgroundColor: '#D4EDDA',
            color: '#155724',
            border: '1px solid #C3E6CB'
          }
        });
        
        // Kaydedilen hesaplamaya scroll
        setTimeout(() => {
          const savedElement = document.getElementById(`saved-calculation-${result.data.id}`);
          if (savedElement) {
            // Önce kaydedilen odalar bölümüne scroll yap
            document.getElementById('saved-calculations-section').scrollIntoView({ behavior: 'smooth' });
            
            // Sonra kaydedilen elemanı vurgula
            savedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Kaydedilen elemanı vurgulamak için geçici bir sınıf ekle
            savedElement.classList.add('highlight-saved');
            
            // 2 saniye sonra vurgu sınıfını kaldır
            setTimeout(() => {
              savedElement.classList.remove('highlight-saved');
            }, 2000);
          }
        }, 100);
      } else if (result.error === 'duplicate_room') {
        // Duplicate room tespit edildi, kullanıcıya seçenek sun
        setDuplicateRoomData({
          existingId: result.existingId,
          projectName: calculationData.project_name,
          roomName: calculationData.room_name
        });
        setPendingSaveData(calculationData);
        setShowDuplicateDialog(true);
      } else {
        toast(t('calculator.notifications.save_failed'), {
          icon: '❌',
          style: {
            backgroundColor: '#F8D7DA',
            color: '#721C24',
            border: '1px solid #F5C6CB'
          }
        });
      }
    } catch (error) {
      console.error('Hesaplama kaydedilirken hata:', error);
      toast(t('calculator.notifications.save_failed'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
    }
  };

  // Duplicate room dialog için handler fonksiyonlar
  const handleUpdateExistingRecord = async () => {
    if (!pendingSaveData) return;
    
    try {
      const result = await saveCalculation(pendingSaveData, true); // updateMode = true
      
      if (result.success) {
        toast(t('calculator.notifications.calculation_updated'), {
          icon: '✅',
          style: {
            backgroundColor: '#D4EDDA',
            color: '#155724',
            border: '1px solid #C3E6CB'
          }
        });
        
        // Güncellenen hesaplamaya scroll
        setTimeout(() => {
          const savedElement = document.getElementById(`saved-calculation-${result.data.id}`);
          if (savedElement) {
            document.getElementById('saved-calculations-section').scrollIntoView({ behavior: 'smooth' });
            savedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            savedElement.classList.add('highlight-saved');
            setTimeout(() => {
              savedElement.classList.remove('highlight-saved');
            }, 2000);
          }
        }, 100);
      } else {
        toast(t('calculator.notifications.save_failed'), {
          icon: '❌',
          style: {
            backgroundColor: '#F8D7DA',
            color: '#721C24',
            border: '1px solid #F5C6CB'
          }
        });
      }
    } catch (error) {
      console.error('Hesaplama güncellenirken hata:', error);
      toast(t('calculator.notifications.save_failed'), {
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
    }
    
    // Dialog'u kapat
    setShowDuplicateDialog(false);
    setDuplicateRoomData(null);
    setPendingSaveData(null);
  };

  const handleCreateNewRecord = () => {
    // Oda adını farklı yapmak için kullanıcıya öner
    if (pendingSaveData) {
      const originalRoomName = pendingSaveData.room_name;
      const timestamp = new Date().toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const newRoomName = `${originalRoomName} (${timestamp})`;
      
      // Oda adını güncelle
      setPendingSaveData({
        ...pendingSaveData,
        room_name: newRoomName,
        room: {
          ...pendingSaveData.room,
          room_name: newRoomName
        }
      });
      
      // Yeni kayıt olarak kaydet
      saveCalculation({
        ...pendingSaveData,
        room_name: newRoomName,
        room: {
          ...pendingSaveData.room,
          room_name: newRoomName
        }
      }, false).then(result => {
        if (result.success) {
          toast(t('calculator.notifications.calculation_saved'), {
            icon: '✅',
            style: {
              backgroundColor: '#D4EDDA',
              color: '#155724',
              border: '1px solid #C3E6CB'
            }
          });
          
          // Kaydedilen hesaplamaya scroll
          setTimeout(() => {
            const savedElement = document.getElementById(`saved-calculation-${result.data.id}`);
            if (savedElement) {
              document.getElementById('saved-calculations-section').scrollIntoView({ behavior: 'smooth' });
              savedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              savedElement.classList.add('highlight-saved');
              setTimeout(() => {
                savedElement.classList.remove('highlight-saved');
              }, 2000);
            }
          }, 100);
        }
      });
    }
    
    // Dialog'u kapat
    setShowDuplicateDialog(false);
    setDuplicateRoomData(null);
    setPendingSaveData(null);
  };

  const handleCancelDuplicateDialog = () => {
    // Mevcut kayda scroll et
    if (duplicateRoomData?.existingId) {
      setTimeout(() => {
        const existingElement = document.getElementById(`saved-calculation-${duplicateRoomData.existingId}`);
        if (existingElement) {
          document.getElementById('saved-calculations-section').scrollIntoView({ behavior: 'smooth' });
          existingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          existingElement.classList.add('highlight-saved');
          setTimeout(() => {
            existingElement.classList.remove('highlight-saved');
          }, 3000);
        }
      }, 100);
    }
    
    // Dialog'u kapat
    setShowDuplicateDialog(false);
    setDuplicateRoomData(null);
    setPendingSaveData(null);
  };

  // Kaydedilen hesaplamayı sil
  const handleDeleteCalculation = async (calculationId) => {
    if (window.confirm(t('calculator.confirmations.delete_calculation'))) {
      try {
        const result = await deleteCalculation(calculationId);
        if (result.success) {
          toast(t('calculator.notifications.calculation_deleted'), {
            icon: '✅',
            style: {
              backgroundColor: '#D4EDDA',
              color: '#155724',
              border: '1px solid #C3E6CB'
            }
          });
        } else {
          toast(t('calculator.notifications.delete_failed'), {
            icon: '❌',
            style: {
              backgroundColor: '#F8D7DA',
              color: '#721C24',
              border: '1px solid #F5C6CB'
            }
          });
        }
      } catch (error) {
        console.error('Hesaplama silinirken hata:', error);
        toast(t('calculator.notifications.delete_failed'), {
          icon: '❌'
        });
      }
    }
  };

  // State for room details popup
  const [selectedRoomDetails, setSelectedRoomDetails] = useState(null);
  const [isRoomDetailsOpen, setIsRoomDetailsOpen] = useState(false);

  // Open room details popup
  const openRoomDetailsPopup = (calculation) => {
    setSelectedRoomDetails(calculation);
    setIsRoomDetailsOpen(true);
  };

  // Close room details popup
  const closeRoomDetailsPopup = () => {
    setSelectedRoomDetails(null);
    setIsRoomDetailsOpen(false);
  };

  // Kaydedilen hesaplamaları göster
  const renderSavedCalculations = () => {
    if (!calculationHistory || calculationHistory.length === 0) {
      return (
        <div className="text-center py-6 text-muted-foreground">
          {t('calculator.no_saved_calculations')}
        </div>
      );
    }

    return (
      <div className="space-y-2 mt-4">
        <div className="flex justify-end mb-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-500 hover:text-red-700 font-normal text-xs px-2 py-1 h-auto"
            onClick={() => {
              if (window.confirm(t('calculator.confirmations.clear_all_calculations'))) {
                clearAllCalculations();
                toast(t('calculator.notifications.all_calculations_cleared'), {
                  icon: '✅',
                  style: {
                    backgroundColor: '#D4EDDA',
                    color: '#155724',
                    border: '1px solid #C3E6CB'
                  }
                });
              }
            }}
          >
            <Trash2 className="h-3 w-3 mr-1" /> {t('calculator.clear_all')}
          </Button>
        </div>
        
        {/* Modern, compact card layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {calculationHistory.map((calculation) => (
            <div 
              key={calculation.id} 
              className="bg-white shadow-sm border rounded-lg p-3 hover:shadow-md hover:bg-gray-50/50 transition-all duration-200"
              id={`saved-calculation-${calculation.id}`}
            >
              {/* Header with room name and delete button */}
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium text-sm text-primary truncate pr-2">
                  {calculation.room.room_name || t('calculator.unnamed_room')}
                </h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0 flex-shrink-0"
                  onClick={() => handleDeleteCalculation(calculation.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              
              {/* Quick info */}
              <div className="space-y-1 mb-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" /> 
                    {new Date(calculation.timestamp).toLocaleDateString('tr-TR')}
                  </span>
                  <span className="flex items-center">
                    <Tag className="h-3 w-3 mr-1" />
                    {(calculation.project_name || calculation.room.project_name || t('calculator.unnamed_project')).substring(0, 15)}
                  </span>
                </div>
                
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">{t('calculator.dimensions')}:</span> {calculation.room.length}×{calculation.room.width}×{calculation.room.height}m
                </div>
                
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">{t('calculator.volume')}:</span> {formatNumber(calculation.room.volume)} m³
                </div>
                
                {calculation.selection && calculation.selection.results && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      <span className="font-medium">{t('calculator.total_extinguishers')}:</span> {
                        calculation.selection.results.products ? 
                          calculation.selection.results.products.filter(p => !p.isController).reduce((sum, p) => sum + p.quantity, 0) :
                          calculation.selection.results.selectedProducts ? 
                            calculation.selection.results.selectedProducts.filter(p => !p.isController).reduce((sum, p) => sum + p.quantity, 0) :
                            '-'
                      } {t('calculator.units')}
                    </span>
                    <span className={`font-medium ${
                      (calculation.selection.results.verificationRatio >= 100) ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {formatNumber(calculation.selection.results.verificationRatio || 0)}%
                    </span>
                  </div>
                )}
              </div>
              
              {/* Action button */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-xs h-7 px-2"
                  onClick={() => openRoomDetailsPopup(calculation)}
                >
                  {t('calculator.view_details')}
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        {calculationHistory.length > 1 && (
          <div className="mt-6 flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              {calculationHistory.length} {t('calculator.saved_rooms_count')}
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-1"
                onClick={handleBulkCopy}
              >
                <Copy className="h-4 w-4 mr-1" /> {t('calculator.bulk_copy')}
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleBulkExport}
              >
                <Download className="h-4 w-4 mr-1" /> {t('calculator.bulk_export')}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Değiştirilmiş CardContent kısmı (örnek olarak greedy algoritması için)
  const renderProductsSection = (products, algorithmKey) => {
    if (!products || products.length === 0) {
      return <div className="text-sm text-muted-foreground">Ürün bulunamadı</div>;
    }
    
    // Kontrol ünitesi hariç tüm ürünleri filtrele
    const extinguishers = products.filter(p => !p.isController);
    
    return (
      <>
        <div className="border rounded-lg overflow-hidden divide-y bg-card">
          {products.map((product, index) => (
            <div key={`${algorithmKey}-${product.id}-${index}`} className="p-4 transition-colors hover:bg-muted/10">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex items-center">
                    <div className="mr-3">
                      <Badge className="bg-primary/90 hover:bg-primary transition-colors">{product.quantity}x</Badge>
                    </div>
                    <div>
                      <h4 className="font-medium">{getProductShortName(getProductById(product.id) || product, i18n.language)}</h4>
                      <p className="text-sm text-muted-foreground">
                        {getProductName(getProductById(product.id) || product, i18n.language)}
                      </p>
                      {/* AFS-CON1 için etkin kütle bilgisini gösterme */}
                      {!product.isController && (
                        <p className="text-sm text-muted-foreground">
                          {t('calculator.effective_mass')}: {formatNumber(product.effective_mass)} g
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Manuel modda kaldır butonu */}
                  {algorithmKey === 'manual' && !product.isController && (
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveManualProduct(product.id)}
                      className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => toggleProductExpand(`${algorithmKey}-${product.id}`)}
                    className="rounded-full h-8 w-8 p-0"
                  >
                    {expandedProducts[`${algorithmKey}-${product.id}`] ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {expandedProducts[`${algorithmKey}-${product.id}`] && (
                <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-muted/5 p-4 rounded-lg">
                  {!product.isController && (
                    <>
                      <div>
                        <p className="text-muted-foreground font-medium mb-1">{t('calculator.total_mass')}</p>
                        <p>{formatNumber(product.mass * product.quantity)} g</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground font-medium mb-1">{t('calculator.total_effective_mass')}</p>
                        <p>{formatNumber(product.effective_mass * product.quantity)} g</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground font-medium mb-1">{t('calculator.extinguishing_capacity')}</p>
                        <p>
                          A: {formatNumber(product.class_a_capacity * product.quantity)} m³, 
                          B: {formatNumber(product.class_b_capacity * product.quantity)} m³
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground font-medium mb-1">{t('calculator.mounting_height')}</p>
                        <p>{product.min_height} - {product.max_height} m</p>
                      </div>
                    </>
                  )}
                  {product.isController && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground font-medium mb-1">Kontrol Ünitesi</p>
                      <p>AFS-CON1 kontrol ünitesi, söndürücülerin sıralı olarak tetiklenmesini sağlar.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Ürün listesinin altına toplam bilgiler ekleniyor - Yeni düzenlenmiş görünüm */}
        <div className="mt-6">
          {/* Toplam ürün bilgileri - Modern tasarım */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-muted/10 p-4 rounded-lg border border-muted shadow-sm">
              <div className="flex flex-col">
                <span className="text-sm text-muted-foreground mb-1">{t('calculator.total_extinguishers')}</span>
                <span className="font-semibold text-2xl">{extinguishers.reduce((sum, p) => sum + p.quantity, 0)}</span>
                <span className="text-xs text-muted-foreground mt-1">{t('calculator.units')}</span>
              </div>
            </div>
            
            <div className="bg-muted/10 p-4 rounded-lg border border-muted shadow-sm">
              <div className="flex flex-col">
                <span className="text-sm text-muted-foreground mb-1">{t('calculator.total_effective_mass')}</span>
                <span className="font-semibold text-2xl">{formatNumber(extinguishers.reduce((sum, p) => sum + (p.effective_mass * p.quantity), 0))}</span>
                <span className="text-xs text-muted-foreground mt-1">gram</span>
              </div>
            </div>
          </div>
          
          {/* Söndürme kapasitesi ve doğrulama oranı - Aşağıya taşındı */}
          {calculationResults && calculationResults.room && (
            <div className="mt-4 pt-4 border-t border-dashed">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/10 p-4 rounded-lg border border-muted shadow-sm">
                  <div className="flex flex-col">
                    <span className="text-sm text-muted-foreground mb-1">{t('calculator.total_suppression_capacity')}</span>
                    <span className="font-semibold text-xl">
                      {calculationResults.room.fire_class === 'b-nheptane-liquid'
                        ? formatNumber(extinguishers.reduce((sum, p) => sum + (p.class_b_capacity * p.quantity), 0))
                        : formatNumber(extinguishers.reduce((sum, p) => sum + (p.class_a_capacity * p.quantity), 0))
                      } m³
                    </span>
                  </div>
                </div>
                
                <div className="bg-muted/10 p-4 rounded-lg border border-muted shadow-sm">
                  <div className="flex flex-col">
                    <span className="text-sm text-muted-foreground mb-1">{t('calculator.verification_ratio')}</span>
                    <div className="flex items-center gap-2">
                      {renderVerificationRatio(
                        {
                          totalClassACapacity: extinguishers.reduce((sum, p) => sum + (p.class_a_capacity * p.quantity), 0),
                          totalClassBCapacity: extinguishers.reduce((sum, p) => sum + (p.class_b_capacity * p.quantity), 0),
                          totalEffectiveMass: extinguishers.reduce((sum, p) => sum + (p.effective_mass * p.quantity), 0)
                        }, 
                        calculationResults.room.volume,
                        calculationResults.room.fire_class,
                        calculationResults.room.required_mass
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => handleCopyResults(algorithmKey)}>
            <Copy className="h-4 w-4 mr-1" /> {t('calculator.copy')}
          </Button>
          <Button 
            size="sm" 
            className="gap-1"
            onClick={() => handleSaveCalculation(algorithmKey)}
          >
            <Save className="h-4 w-4 mr-1" /> {t('calculator.save')}
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            className="gap-1 bg-green-600 hover:bg-green-700 text-white"
            onClick={() => exportToExcel(
              calculationResults, 
              calculationResults.project_name || 'Proje', 
              calculationResults.room_name || 'Oda',
              t,
              i18n.language
            )}
          >
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </>
    );
  };

  // Scroll fonksiyonu
  const handleScrollToggle = () => {
    if (isAtTop) {
      // Kaydedilen odalar bölümüne scroll et
      const savedSection = document.getElementById('saved-calculations-section');
      if (savedSection) {
        savedSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Sayfanın üstüne scroll et
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary border-solid mx-auto mb-4"></div>
          <p className="text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Aktif seçimi al
  const activeSelection = getActiveSelection();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 pt-4 sm:pt-6 lg:pt-8 pb-8 sm:pb-12 lg:pb-16">
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
        {error && (
            <Alert variant="destructive" className="mb-4 sm:mb-6 lg:mb-8 animate-fade-in">
            <AlertDescription className="text-sm sm:text-base">{error}</AlertDescription>
          </Alert>
        )}

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
          {/* Sol kolon - Hesaplama formu */}
            <div className="xl:col-span-5 animate-slide-up">
              <div className="xl:sticky xl:top-20">
            <CalculatorForm 
              onCalculationResults={handleCalculationResults}
              onError={handleError}
            />
              </div>
            </div>
            
            {/* Sağ kolon - Hesaplama sonuçları ve ürün seçimleri */}
            <div className="xl:col-span-7">
              {calculationResults && calculationResults.room ? (
                <div className="space-y-4 sm:space-y-5 lg:space-y-6 animate-slide-up">
                  {/* Oda Bilgileri */}
                  <Card className="overflow-hidden border-muted shadow-sm">
                    <CardHeader className="bg-muted/30 pb-3 px-3 sm:px-6">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                        <CardTitle className="text-lg sm:text-xl">{t('calculator.calculation_details')}</CardTitle>
                      </div>
                      <CardDescription className="text-sm sm:text-base">
                        {calculationResults.room.fire_class === 'a-wooden-stack' && t('calculator.fire_class_a')}
                        {calculationResults.room.fire_class === 'b-nheptane-liquid' && t('calculator.fire_class_b')}
                        {calculationResults.room.fire_class === 'a-polymer-sheet' && t('calculator.fire_class_a_polymer')}
                        {calculationResults.room.fire_class === 'wooden-stack-general' && t('calculator.fire_class_wooden_general')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4 sm:pt-0 px-3 sm:px-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 sm:gap-y-4 gap-x-4 sm:gap-x-6">
                        <div>
                          <div className="flex items-start gap-1.5 text-xs sm:text-sm text-muted-foreground min-h-[2.5rem]">
                            <Ruler className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0 mt-0.5" />
                            <span className="leading-tight">{t('calculator.total_volume')}</span>
                          </div>
                          <p className="text-lg sm:text-xl font-semibold mt-0">{formatNumber(calculationResults.room.volume)} m³</p>
                        </div>
                        <div>
                          <div className="flex items-start gap-1.5 text-xs sm:text-sm text-muted-foreground min-h-[2.5rem]">
                            <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0 mt-0.5" />
                            <span className="leading-tight">{t('calculator.adjusted_volume_with_safety')}</span>
                          </div>
                          <p className="text-lg sm:text-xl font-semibold mt-0">{formatNumber(calculationResults.room.adjusted_volume)} m³</p>
                        </div>
                        <div>
                          <div className="flex items-start gap-1.5 text-xs sm:text-sm text-muted-foreground min-h-[2.5rem]">
                            <Layers className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0 mt-0.5" />
                            <span className="leading-tight">{t('calculator.safety_factor_value')}</span>
                          </div>
                          <p className="text-lg sm:text-xl font-semibold mt-0">{formatNumber(calculationResults.room.safety_factor)}</p>
                        </div>
                        <div>
                          <div className="flex items-start gap-1.5 text-xs sm:text-sm text-muted-foreground min-h-[2.5rem]">
                            <Flame className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0 mt-0.5" />
                            <span className="leading-tight">{t('calculator.required_suppressant')}</span>
                          </div>
                          <p className="text-lg sm:text-xl font-semibold mt-0">{formatNumber(calculationResults.room.required_mass)} g</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Seçim Algoritmaları Tabları */}
                  {calculationResults.selections && (
                    <Card className="border-muted shadow-sm overflow-hidden" id="calculation-results">
                      <CardHeader className="bg-muted/30 pb-3 px-3 sm:px-6">
                        <div className="flex items-center gap-2">
                          <CalculatorIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                          <CardTitle className="text-lg sm:text-xl">{t('calculator.selected_products')}</CardTitle>
                        </div>
                        <CardDescription className="text-sm sm:text-base">{t('calculator.selection_explanation')}</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-4 sm:pt-5 px-3 sm:px-6">
                        <Tabs value={selectedMode} onValueChange={handleModeChange} className="w-full">
                          <TabsList className="mb-4 w-full grid grid-cols-2 h-9 sm:h-10">
                            <TabsTrigger value="automatic">{t('calculator.automatic_selection')}</TabsTrigger>
                            <TabsTrigger value="manual">{t('calculator.manual_selection')}</TabsTrigger>
                          </TabsList>
                          
                          {/* Otomatik seçim tab'ı */}
                          <TabsContent value="automatic">
                            <div className="space-y-5">
                              {/* Otomatik algoritma sonuçlarını göster */}
                              {calculationResults.appliedSelection && calculationResults.appliedSelection.algorithm !== 'manual' ? (
                                renderProductsSection(calculationResults.appliedSelection.results.selectedProducts, calculationResults.appliedSelection.algorithm)
                              ) : calculationResults.selections?.new_dynamic_safe ? (
                                renderProductsSection(calculationResults.selections.new_dynamic_safe.products, 'new_dynamic_safe')
                              ) : calculationResults.selections?.excel_complete ? (
                                renderProductsSection(calculationResults.selections.excel_complete.products, 'excel_complete')
                              ) : calculationResults.selections?.greedy ? (
                                renderProductsSection(calculationResults.selections.greedy.products, 'greedy')
                              ) : (
                                <p className="text-muted-foreground text-center py-4">
                                  {t('calculator.no_automatic_results')}
                                </p>
                              )}
                            </div>
                          </TabsContent>
                          
                          {/* Manuel seçim tab'ı */}
                          <TabsContent value="manual">
                            <div className="space-y-4 sm:space-y-5">
                              {/* Manuel ürün seçimi bölümü */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4 bg-muted/30 rounded-lg border">
                                <div>
                                  <label className="text-sm font-medium mb-1 block">{t('calculator.product_model')}</label>
                                  <Select
                                    value={manualProductId}
                                    onValueChange={setManualProductId}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder={t('calculator.select_product')}>
                                        {manualProductId && 
                                          (() => {
                                            const product = getProductById(manualProductId);
                                            if (product) {
                                              return product.model;
                                            }
                                            return t('calculator.select_product');
                                          })()
                                        }
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {getExtinguisherProducts().map(product => (
                                        <SelectItem key={product.id} value={product.id.toString()}>
                                          <div className="truncate max-w-[220px]">
                                            {product.model} - {getProductName(product, i18n.language)}
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium mb-1 block">{t('calculator.quantity')}</label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={manualQuantity}
                                    onChange={(e) => setManualQuantity(parseInt(e.target.value) || 1)}
                                    placeholder={t('calculator.quantity_placeholder')}
                                  />
                                </div>
                                
                                <div className="flex items-end">
                                  <Button onClick={handleAddManualProduct} className="w-full">
                                    {t('calculator.add_button')}
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Seçilen manuel ürünler listesi */}
                              {manualSelections.length > 0 || (calculationResults.appliedSelection && calculationResults.appliedSelection.algorithm === 'manual') ? (
                                renderProductsSection(
                                  calculationResults.appliedSelection?.algorithm === 'manual' 
                                    ? calculationResults.appliedSelection.results.selectedProducts 
                                    : manualSelections, 
                                  'manual'
                                )
                              ) : (
                                <p className="text-muted-foreground text-center py-4">
                                  {t('calculator.no_manual_products')}
                                </p>
                              )}
                            </div>
                          </TabsContent>
                          
                          {/* Excel Complete Algorithm sonuçları */}
                          {calculationResults.selections?.excel_complete && (
                            <TabsContent value="excel_complete">
                              <div className="space-y-5">
                                {/* Ürün Listesi - Excel Complete */}
                                {renderProductsSection(calculationResults.selections.excel_complete.products, 'excel_complete')}
                              </div>
                            </TabsContent>
                          )}
                          
                          {calculationResults.selections?.greedy && (
                            <TabsContent value="greedy">
                              <div className="space-y-5">
                                {/* Ürün Listesi - Greedy */}
                                {renderProductsSection(calculationResults.selections.greedy.products, 'greedy')}
                              </div>
                            </TabsContent>
                          )}
                        </Tabs>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="h-[70vh] flex items-center justify-center rounded-lg border border-dashed p-8 text-center animate-pulse">
                  <div>
                    <div className="bg-muted/30 rounded-full p-6 mx-auto mb-5 w-fit">
                      <CalculatorIcon className="h-12 w-12 text-primary/50" />
                    </div>
                    <h3 className="text-xl font-medium mb-2">{t('calculator.no_results_yet')}</h3>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                      {t('calculator.fill_form_to_calculate')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Kaydedilen Hesaplamalar Bölümü - Tam genişlikte, sayfanın altında */}
          <div className="mt-8 sm:mt-10 lg:mt-12 pt-6 sm:pt-8 border-t">
            <Card className="border-muted shadow-sm overflow-hidden w-full" id="saved-calculations-section">
              <CardHeader className="bg-muted/30 pb-3 px-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Save className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                    <CardTitle className="text-lg sm:text-xl">{t('calculator.saved_calculations')}</CardTitle>
                  </div>
                </div>
                <CardDescription className="text-sm sm:text-base">
                  {t('calculator.saved_calculation_count', { count: calculationHistory?.length || 0 })}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-4 sm:pt-5 px-3 sm:px-6">
                {renderSavedCalculations()}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <footer className="py-6 sm:py-8 border-t bg-muted/20">
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                <div className="h-5 w-5 sm:h-6 sm:w-6 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                  <img 
                    src={alasAmblem} 
                    alt="Alas Fire Amblem" 
                    className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary-foreground"
                  />
                </div>
                <span className="font-semibold text-sm sm:text-base">ADC Makine Sanayi ve Ticaret Limited Şirketi</span>
              </div>
              <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Tüm hakları saklıdır.</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 lg:gap-8">
              <Link 
                to="/privacy-policy" 
                className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('privacy.title')}
              </Link>
              <Link 
                to="/terms-of-service" 
                className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('terms.title')}
              </Link>
              <a 
                href="https://www.alasfire.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-primary hover:underline flex items-center"
              >
                <span>alasfire.com</span>
                <ExternalLink className="h-3 w-3 ml-1 flex-shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Scroll Toggle Button - Sayfanın sağ alt köşesinde sabit */}
      <Button
        onClick={handleScrollToggle}
        className="fixed bottom-16 right-4 sm:bottom-8 sm:right-6 lg:bottom-6 lg:right-6 h-10 w-10 sm:h-12 sm:w-12 rounded-full shadow-lg z-50 p-0 bg-primary hover:bg-primary/90 text-primary-foreground"
        title={isAtTop ? t('calculator.scroll_to_saved_rooms') : t('calculator.scroll_to_top')}
      >
        {isAtTop ? (
          <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />
        ) : (
          <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5" />
        )}
      </Button>

      {/* Duplicate Room Dialog */}
      <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <AlertDialogContent className="max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto mx-3">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Building className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
              {t('calculator.confirmations.duplicate_room_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-xs sm:text-sm">
              {t('calculator.confirmations.duplicate_room_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {duplicateRoomData && pendingSaveData && (
            <div className="space-y-6 py-4">
              {/* Proje ve Oda Bilgileri */}
              <Card className="border-muted">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Tag className="h-4 w-4 text-primary" />
                    {t('calculator.project_info')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                      {t('calculator.project_name')}
                    </label>
                    <p className="text-sm sm:text-base font-semibold">{duplicateRoomData.projectName}</p>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                      {t('calculator.room_name')}
                    </label>
                    <p className="text-sm sm:text-base font-semibold">{duplicateRoomData.roomName}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Yeni Hesaplama Detayları */}
              {pendingSaveData.room && (
                <Card className="border-muted">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CalculatorIcon className="h-4 w-4 text-primary" />
                      {t('calculator.calculation_details')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 sm:space-y-4">
                    {/* Oda Boyutları */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <div>
                        <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                          {t('calculator.length')}
                        </label>
                        <p className="text-sm sm:text-base font-semibold">{formatNumber(pendingSaveData.room.length)} m</p>
                      </div>
                      <div>
                        <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                          {t('calculator.width')}
                        </label>
                        <p className="text-sm sm:text-base font-semibold">{formatNumber(pendingSaveData.room.width)} m</p>
                      </div>
                      <div>
                        <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                          {t('calculator.height')}
                        </label>
                        <p className="text-sm sm:text-base font-semibold">{formatNumber(pendingSaveData.room.height)} m</p>
                      </div>
                      <div>
                        <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                          {t('calculator.volume')}
                        </label>
                        <p className="text-sm sm:text-base font-semibold">{formatNumber(pendingSaveData.room.volume)} m³</p>
                      </div>
                    </div>
                    
                    {/* Yangın Sınıfı ve Emniyet Faktörü */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          {t('calculator.fire_class')}
                        </label>
                        <p className="text-base font-semibold">
                          {t(`calculator.fire_class_options.${pendingSaveData.room.fire_class}`)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          {t('calculator.safety_factor')}
                        </label>
                        <p className="text-base font-semibold">{formatNumber(pendingSaveData.room.safety_factor)}</p>
                      </div>
                    </div>
                    
                    {/* Gerekli Söndürücü Miktarı */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        {t('calculator.required_suppressant')}
                      </label>
                      <p className="text-lg font-bold text-primary">{formatNumber(pendingSaveData.room.required_mass)} g</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Seçilen Ürünler */}
              {pendingSaveData.selection?.results && (
                <Card className="border-muted">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" />
                      {t('calculator.selected_products')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(pendingSaveData.selection.results.selectedProducts || pendingSaveData.selection.results.products) ? (
                      <div className="space-y-3">
                        {(pendingSaveData.selection.results.selectedProducts || pendingSaveData.selection.results.products || []).map((product, index) => {
                          // Ürün adını güvenli şekilde al
                          const productObj = getProductById(product.id) || product;
                          const productName = getProductName(productObj, i18n.language) || product.model || 'Unknown Product';
                          
                          return (
                            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                              <div>
                                <p className="font-medium">{product.quantity}x {productName}</p>
                                <p className="text-sm text-muted-foreground">
                                  {t('calculator.effective_mass')}: {formatNumber(product.effective_mass)} g
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">
                                  {formatNumber(product.quantity * product.effective_mass)} g
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Toplam ve Doğrulama Oranı */}
                        <Separator />
                        <div className="flex justify-between items-center font-semibold">
                          <span>{t('calculator.total_effective_mass')}:</span>
                          <span className="text-primary">
                            {formatNumber(pendingSaveData.selection.results.totalMass)} g
                          </span>
                        </div>
                        
                        {pendingSaveData.selection.results.verificationRatio && (
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{t('calculator.verification_ratio')}:</span>
                            <Badge 
                              variant={
                                pendingSaveData.selection.results.verificationRatio >= 124 ? "default" :
                                pendingSaveData.selection.results.verificationRatio >= 100 ? "secondary" : "destructive"
                              }
                            >
                              {formatNumber(pendingSaveData.selection.results.verificationRatio)}%
                            </Badge>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        {t('calculator.no_products_selected')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={handleCancelDuplicateDialog} className="w-full sm:w-auto text-sm">
              {t('calculator.confirmations.cancel')}
            </AlertDialogCancel>
            <Button 
              onClick={handleCreateNewRecord}
              variant="outline"
              className="w-full sm:w-auto text-sm"
            >
              {t('calculator.confirmations.create_new_record')}
            </Button>
            <AlertDialogAction 
              onClick={handleUpdateExistingRecord}
              className="w-full sm:w-auto text-sm"
            >
              {t('calculator.confirmations.update_existing_record')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Room Details Popup */}
      <AlertDialog open={isRoomDetailsOpen} onOpenChange={setIsRoomDetailsOpen}>
        <AlertDialogContent className="max-w-xs sm:max-w-sm md:max-w-3xl lg:max-w-4xl max-h-[90vh] overflow-y-auto mx-3">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Building className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
              {t('calculator.room_details')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-xs sm:text-sm">
              {selectedRoomDetails?.room?.room_name || t('calculator.unnamed_room')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {selectedRoomDetails && (
            <div className="space-y-6 py-4">
              {/* Proje ve Genel Bilgiler */}
              <Card className="border-muted">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Tag className="h-4 w-4 text-primary" />
                    {t('calculator.project_info')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                      {t('calculator.room_name')}
                    </label>
                    <p className="text-sm sm:text-base font-semibold">
                      {selectedRoomDetails.room.room_name || t('calculator.unnamed_room')}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                      {t('calculator.project_name')}
                    </label>
                    <p className="text-sm sm:text-base font-semibold">
                      {selectedRoomDetails.project_name || selectedRoomDetails.room.project_name || t('calculator.unnamed_project')}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                      {t('calculator.calculation_date')}
                    </label>
                    <p className="text-sm sm:text-base font-semibold">
                      {new Date(selectedRoomDetails.timestamp).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  
                  {/* Bölge ve Firma Bilgileri */}
                  {selectedRoomDetails.region_name && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        {t('calculator.region')}
                      </label>
                      <p className="text-base font-semibold">{selectedRoomDetails.region_name}</p>
                    </div>
                  )}
                  {selectedRoomDetails.company_name && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        {t('calculator.company')}
                      </label>
                      <p className="text-base font-semibold">{selectedRoomDetails.company_name}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Oda Özellikleri */}
              <Card className="border-muted">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Ruler className="h-4 w-4 text-primary" />
                    {t('calculator.room_properties')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  {/* Boyutlar */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                        {t('calculator.length')}
                      </label>
                      <p className="text-sm sm:text-base font-semibold">{formatNumber(selectedRoomDetails.room.length)} m</p>
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                        {t('calculator.width')}
                      </label>
                      <p className="text-sm sm:text-base font-semibold">{formatNumber(selectedRoomDetails.room.width)} m</p>
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                        {t('calculator.height')}
                      </label>
                      <p className="text-sm sm:text-base font-semibold">{formatNumber(selectedRoomDetails.room.height)} m</p>
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                        {t('calculator.volume')}
                      </label>
                      <p className="text-sm sm:text-base font-semibold">{formatNumber(selectedRoomDetails.room.volume)} m³</p>
                    </div>
                  </div>
                  
                  {/* Hesaplama Parametreleri */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        {t('calculator.fire_class')}
                      </label>
                      <p className="text-base font-semibold">
                        {selectedRoomDetails.room.fire_class === 'a-wooden-stack' && t('calculator.fire_class_a_short')}
                        {selectedRoomDetails.room.fire_class === 'b-nheptane-liquid' && t('calculator.fire_class_b_short')}
                        {selectedRoomDetails.room.fire_class === 'a-polymer-sheet' && t('calculator.fire_class_a_polymer_short')}
                        {selectedRoomDetails.room.fire_class === 'wooden-stack-general' && t('calculator.fire_class_wooden_short')}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        {t('calculator.safety_factor')}
                      </label>
                      <p className="text-base font-semibold">{formatNumber(selectedRoomDetails.room.safety_factor)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        {t('calculator.adjusted_volume')}
                      </label>
                      <p className="text-base font-semibold">{formatNumber(selectedRoomDetails.room.adjusted_volume)} m³</p>
                    </div>
                  </div>
                  
                  {/* Gerekli Söndürücü */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('calculator.required_extinguisher')}
                    </label>
                    <p className="text-lg font-bold text-primary">{formatNumber(selectedRoomDetails.room.required_mass)} g</p>
                  </div>
                </CardContent>
              </Card>

              {/* Seçilen Ürünler */}
              {selectedRoomDetails.selection?.results && (
                <Card className="border-muted">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" />
                      {t('calculator.selected_products')} ({selectedRoomDetails.selectedAlgorithm || 'Otomatik'})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(selectedRoomDetails.selection.results.selectedProducts || selectedRoomDetails.selection.results.products) ? (
                      <div className="space-y-3">
                        {(selectedRoomDetails.selection.results.selectedProducts || selectedRoomDetails.selection.results.products || []).map((product, index) => {
                          if (product.isController) return null; // Kontrol ünitelerini atla
                          
                          const productObj = getProductById(product.id) || product;
                          const productName = getProductName(productObj, i18n.language) || product.model || 'Unknown Product';
                          
                          return (
                            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                              <div className="flex-1">
                                <p className="font-medium">{product.quantity}x {productName}</p>
                                <p className="text-sm text-muted-foreground">
                                  {t('calculator.effective_mass')}: {formatNumber(product.effective_mass)} g / {t('calculator.units')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">
                                  {formatNumber(product.quantity * product.effective_mass)} g
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t('calculator.total_mass')}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Kontrol Ünitesi */}
                        {selectedRoomDetails.selection.results.products?.find(p => p.isController) && (
                          <div className="border-t pt-3">
                            <h4 className="font-medium text-sm text-muted-foreground mb-2">{t('calculator.control_unit')}</h4>
                            {selectedRoomDetails.selection.results.products.filter(p => p.isController).map((controller, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                                <div>
                                  <p className="font-medium text-sm">{controller.quantity}x {controller.model}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Özet */}
                        <Separator />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                          <div className="text-center p-3 bg-muted/20 rounded-lg">
                            <p className="text-sm font-medium text-muted-foreground">{t('calculator.total_extinguishers')}</p>
                            <p className="text-lg font-bold text-primary">
                              {(selectedRoomDetails.selection.results.selectedProducts || selectedRoomDetails.selection.results.products || [])
                                .filter(p => !p.isController)
                                .reduce((sum, p) => sum + p.quantity, 0)} {t('calculator.units')}
                            </p>
                          </div>
                          
                          <div className="text-center p-3 bg-muted/20 rounded-lg">
                            <p className="text-sm font-medium text-muted-foreground">{t('calculator.total_mass')}</p>
                            <p className="text-lg font-bold text-primary">
                              {formatNumber(selectedRoomDetails.selection.results.totalMass || 0)} g
                            </p>
                          </div>
                          
                          <div className="text-center p-3 bg-muted/20 rounded-lg">
                            <p className="text-sm font-medium text-muted-foreground">{t('calculator.validation_ratio')}</p>
                            <p className={`text-lg font-bold ${
                              (selectedRoomDetails.selection.results.verificationRatio >= 100) ? 'text-green-600' : 'text-red-500'
                            }`}>
                              {formatNumber(selectedRoomDetails.selection.results.verificationRatio || 0)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        {t('calculator.no_products_selected')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={closeRoomDetailsPopup} className="w-full sm:w-auto text-sm">
              {t('calculator.close')}
            </AlertDialogCancel>
            <Button 
              onClick={() => {
                if (selectedRoomDetails) {
                  setCalculationResults({
                    room: selectedRoomDetails.room,
                    selections: selectedRoomDetails.selection?.results ? 
                      { [selectedRoomDetails.selectedAlgorithm]: selectedRoomDetails.selection.results } : 
                      null,
                    appliedSelection: selectedRoomDetails.selection
                  });
                  window.scrollTo({
                    top: document.getElementById('calculation-results')?.offsetTop - 100 || 0,
                    behavior: 'smooth'
                  });
                  closeRoomDetailsPopup();
                }
              }}
              variant="default"
              className="w-full sm:w-auto text-sm"
            >
              {t('calculator.load_calculation')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Calculator; 